/**
 * MiniFactorRadar — Compact radar + rationale preview shown on hover.
 * Uses only data embedded in the pick object (no API calls on hover).
 */
import React, { useState } from 'react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from 'recharts';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { factorMetaSingular, scoreColor } from './utils';

// Pretty labels for raw metric keys to avoid showing 'pe_vs_sector' etc.
const METRIC_LABELS = {
  pe_vs_sector: 'PE vs Sector',
  pb: 'P/B Ratio',
  roe_pct: 'ROE',
  profit_margin_pct: 'Profit Margin',
  vol_adj_20d: 'Vol-adj 20d',
  vol_adj_60d: 'Vol-adj 60d',
  rs_20d_pct: 'RS vs NIFTY 20d',
  rs_60d_pct: 'RS vs NIFTY 60d',
  vs_sma50_pct: 'vs SMA-50',
  vs_sma200_pct: 'vs SMA-200',
  rsi_14: 'RSI (14)',
  macd_bullish: 'MACD',
  bb_position: 'Bollinger Band %',
  sma_stack: 'SMA Stack',
  volume_ratio: 'Volume Ratio',
  pct_from_52w_high: 'From 52W High',
  market_cap_cr: 'Market Cap (₹ Cr)',
};

// Metrics displayed as percentages (add % suffix)
const PCT_METRICS = new Set([
  'roe_pct', 'profit_margin_pct', 'rs_20d_pct', 'rs_60d_pct',
  'vs_sma50_pct', 'vs_sma200_pct', 'bb_position', 'pct_from_52w_high',
]);

const formatMetricValue = (key, v) => {
  if (v === null || v === undefined) return '–';
  if (typeof v === 'boolean') return v ? 'Bullish' : 'Bearish';
  if (typeof v === 'string') {
    return v.charAt(0).toUpperCase() + v.slice(1);
  }
  if (typeof v !== 'number') return String(v);
  if (key === 'market_cap_cr') return v >= 100000 ? `${(v / 100000).toFixed(1)}L Cr` : `${Math.round(v).toLocaleString('en-IN')} Cr`;
  if (key === 'pe_vs_sector') return `${v.toFixed(2)}x`;
  if (key === 'pb') return v.toFixed(2);
  if (key === 'vol_adj_20d' || key === 'vol_adj_60d') return v.toFixed(4);
  if (key === 'rsi_14' || key === 'volume_ratio') return v.toFixed(2);
  if (PCT_METRICS.has(key)) return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
  return v.toFixed(2);
};

const MiniFactorRadar = ({ pick }) => {
  const [showRaw, setShowRaw] = useState(false);

  if (!pick || !pick.factor_scores) return null;

  const data = Object.entries(pick.factor_scores).map(([k, v]) => ({
    factor: factorMetaSingular[k]?.label || k,
    score: v,
  }));

  const factorEntries = Object.entries(pick.factor_scores);
  const factorDetails = pick.factor_details || {};
  const hasDetails = Object.values(factorDetails).some(
    (v) => v && typeof v === 'object' && Object.keys(v).length > 0
  );

  return (
    <div className="w-[28rem] bg-popover border border-border shadow-2xl p-3 text-popover-foreground" data-testid="mini-factor-radar">
      {/* Header */}
      <div className="flex items-start justify-between mb-2 gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm text-foreground truncate">{pick.symbol}</div>
          <div className="text-[10px] text-muted-foreground truncate">
            {(pick.name || '').replace(' Limited', '').replace(' Ltd', '')}
            {pick.sector ? ` · ${pick.sector}` : ''}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[8px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">
            Composite
          </div>
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-bold border ${scoreColor(
              pick.composite_score
            )}`}
            title="Composite score = weighted average of Value, Quality, Momentum, Technical, Smart-$ factor scores"
          >
            {Number(pick.composite_score).toFixed(1)}
          </span>
        </div>
      </div>

      {/* Radar */}
      <div className="h-[180px] -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="72%">
            <PolarGrid stroke="#30363d" />
            <PolarAngleAxis dataKey="factor" tick={{ fill: '#9ca3af', fontSize: 9 }} />
            <PolarRadiusAxis domain={[0, 100]} tick={false} stroke="#30363d" axisLine={false} />
            <Radar name={pick.symbol} dataKey="score" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.35} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Factor score chips — disambiguates composite vs individual factor scores */}
      <div className="flex flex-wrap gap-1.5 mt-1 mb-1">
        {factorEntries.map(([k, v]) => {
          const meta = factorMetaSingular[k] || { label: k, color: '#6b7280' };
          return (
            <div
              key={k}
              className="flex items-center gap-1 px-1.5 py-0.5 bg-surface-highlight border border-border/50 rounded-sm text-[10px]"
              title={`${meta.label} factor score: ${Math.round(v)}/100`}
            >
              <span className="text-muted-foreground">{meta.label}</span>
              <span className="font-mono font-bold" style={{ color: meta.color }}>
                {Math.round(v)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Rationale (full text, no clamp) */}
      {pick.rationale ? (
        <div className="mt-2 border-t border-border pt-2">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3 h-3 text-accent" />
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
              AI Rationale
            </span>
          </div>
          <p className="text-[11px] text-foreground leading-relaxed">{pick.rationale}</p>
        </div>
      ) : (
        <div className="mt-2 border-t border-border pt-2 text-[10px] text-muted-foreground italic">
          Click the row to open full detail + generate rationale.
        </div>
      )}

      {/* Raw factor details — expandable audit trail */}
      {hasDetails && (
        <div className="mt-2 border-t border-border pt-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowRaw(!showRaw); }}
            className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-semibold hover:text-foreground transition-colors"
            data-testid="raw-details-toggle"
          >
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Raw Factor Details
            </span>
            {showRaw ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {showRaw && (
            <div className="mt-2 space-y-2" data-testid="raw-details-content">
              {Object.entries(factorDetails).map(([factorKey, metrics]) => {
                if (!metrics || typeof metrics !== 'object' || !Object.keys(metrics).length) return null;
                const meta = factorMetaSingular[factorKey] || { label: factorKey, color: '#6b7280' };
                const factorScore = pick.factor_scores?.[factorKey];
                return (
                  <div key={factorKey} className="bg-surface-highlight border border-border/50 rounded-sm p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: meta.color }}
                      >
                        {meta.label}
                      </span>
                      <span
                        className="text-[10px] font-mono font-bold"
                        style={{ color: meta.color }}
                      >
                        {Math.round(factorScore)}/100
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                      {Object.entries(metrics).map(([k, v]) => (
                        <div key={k} className="flex justify-between items-center text-[10px]">
                          <span className="text-muted-foreground truncate" title={k}>
                            {METRIC_LABELS[k] || k.replace(/_/g, ' ')}
                          </span>
                          <span className="font-mono text-foreground ml-2 shrink-0">
                            {formatMetricValue(k, v)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MiniFactorRadar;
